import { useEffect, useCallback, useRef, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { store } from '@/store/store'
import { socketService } from '@/services/socketService'
import { transferTempSession, updateStreamingMessageId, updateMessage, updateSessionTitle } from '@/store/slices/hrChatSlice'
import { updateStreamingMessage, startStreamingMessage, addStreamingChunk, streamingCompleted as opsStreamingCompleted, transferTempSession as transferOpsSession, streamingError } from '@/store/slices/opsChatSlice'
import type { 
  MessageChunkData,
  MessageCompleteData,
  MessageErrorData,
  MessageStartData,
  CitationData,
  StatusData,
  SendMessageData,
  TypingData
} from '@/services/socketService'
import {
  connectionStarted,
  connectionSuccess,
  connectionError,
  connectionLost,
  connectionReconnecting,
  connectionRestored,
  connectionFailed,
  connectionTerminated,
  sessionJoined,
  messageStreamStarted,
  messageChunkReceived,
  messageCitationReceived,
  messageStatusUpdated,
  messageStreamCompleted,
  messageStreamError,
  clearStreamingMessage,
  userTypingStarted,
  userTypingStopped,
  clearOldTypingIndicators
} from '@/store/slices/socketSlice'

export interface UseWebSocketOptions {
  autoConnect?: boolean
  sessionId?: string
}

export interface WebSocketActions {
  connect: () => Promise<void>
  disconnect: () => void
  joinSession: (sessionId: string) => void
  leaveSession: (sessionId: string) => void
  sendMessage: (data: SendMessageData) => void
  sendTyping: (isTyping: boolean, sessionId: string) => void
  cancelMessage: (messageId: string) => void
  isConnected: () => boolean
}

export const useWebSocket = (options: UseWebSocketOptions = {}): WebSocketActions => {
  const { autoConnect = false, sessionId } = options
  const dispatch = useAppDispatch()
  const { token } = useAppSelector(state => state.auth)
  const { connection } = useAppSelector(state => state.socket)
  
  const cleanupFunctions = useRef<Array<() => void>>([])
  const typingTimeout = useRef<NodeJS.Timeout>()
  const joinedSessionsRef = useRef<Set<string>>(new Set())
  const sessionIdRef = useRef<string | undefined>(sessionId)

  // Connection management
  const connect = useCallback(async () => {
    if (!token) {
      console.warn('No token available for WebSocket connection')
      return
    }

    if (connection.connected || connection.connecting) {
      return
    }

    try {
      dispatch(connectionStarted())
      await socketService.connect(token)
    } catch (error) {
      console.error('WebSocket connection failed:', error)
    }
  }, [token, connection.connected, connection.connecting, dispatch])

  const disconnect = useCallback(() => {
    socketService.disconnect()
  }, [])

  // Session management
  const joinSession = useCallback((sessionId: string) => {
    socketService.joinSession(sessionId)
  }, [])

  const leaveSession = useCallback((sessionId: string) => {
    socketService.leaveSession(sessionId)
  }, [])

  // Message actions
  const sendMessage = useCallback((data: SendMessageData) => {
    socketService.sendMessage(data)
  }, [])

  const sendTyping = useCallback((isTyping: boolean, sessionId: string) => {
    if (typingTimeout.current) {
      clearTimeout(typingTimeout.current)
    }

    const typingData: TypingData = { sessionId, isTyping }
    socketService.sendTyping(typingData)

    if (isTyping) {
      // Auto-stop typing after 3 seconds
      typingTimeout.current = setTimeout(() => {
        socketService.sendTyping({ sessionId, isTyping: false })
      }, 3000)
    }
  }, [])

  const cancelMessage = useCallback((messageId: string) => {
    socketService.cancelMessage(messageId)
  }, [])

  const isConnected = useCallback(() => {
    return socketService.isConnected()
  }, [])

  // Keep sessionIdRef in sync with sessionId prop
  useEffect(() => {
    sessionIdRef.current = sessionId
  }, [sessionId])

  // Setup event listeners
  useEffect(() => {
    // Connection events
    const cleanupConnection = socketService.onConnectionEvent('connection:success', () => {
      dispatch(connectionSuccess())
    })
    cleanupFunctions.current.push(cleanupConnection)

    const cleanupError = socketService.onConnectionEvent('connection:error', (data) => {
      dispatch(connectionError({ error: data.error }))
    })
    cleanupFunctions.current.push(cleanupError)

    const cleanupLost = socketService.onConnectionEvent('connection:lost', (data) => {
      dispatch(connectionLost({ reason: data.reason }))
    })
    cleanupFunctions.current.push(cleanupLost)

    const cleanupReconnecting = socketService.onConnectionEvent('connection:reconnecting', (data) => {
      dispatch(connectionReconnecting({ attempt: data.attempt }))
    })
    cleanupFunctions.current.push(cleanupReconnecting)

    const cleanupRestored = socketService.onConnectionEvent('connection:restored', (data) => {
      dispatch(connectionRestored({ attempts: data.attempts }))
    })
    cleanupFunctions.current.push(cleanupRestored)

    const cleanupFailed = socketService.onConnectionEvent('connection:failed', () => {
      dispatch(connectionFailed())
    })
    cleanupFunctions.current.push(cleanupFailed)

    const cleanupTerminated = socketService.onConnectionEvent('connection:terminated', () => {
      dispatch(connectionTerminated())
    })
    cleanupFunctions.current.push(cleanupTerminated)

    // Handle immediate session creation - this switches the active session right away
    socketService.on('chat:session-created', (data) => {
      console.log('🆕 New session created:', data.sessionId, 'replacing temp:', data.originalTempSessionId)
      
      // Determine which agent type based on active session
      const currentState = store.getState()
      const hrActiveSessionId = currentState.hrChat.activeSessionId
      const opsActiveSessionId = currentState.opsChat.activeSessionId
      
      if (hrActiveSessionId === data.originalTempSessionId) {
        // Transfer HR session
        dispatch(transferTempSession({
          tempSessionId: data.originalTempSessionId,
          realSessionId: data.sessionId
        }))
      } else if (opsActiveSessionId === data.originalTempSessionId) {
        // Transfer Ops session
        dispatch(transferOpsSession({
          tempSessionId: data.originalTempSessionId,
          realSessionId: data.sessionId
        }))
      }
    })

    // Socket server events - prevent duplicate joins with a ref
    socketService.on('chat:joined', (data) => {
      console.log('📨 Received chat:joined:', data.sessionId)
      
      // Only join if we haven't already joined this session
      if (!joinedSessionsRef.current.has(data.sessionId)) {
        joinedSessionsRef.current.add(data.sessionId)
        dispatch(sessionJoined({ sessionId: data.sessionId }))
        // Auto-join the session room to receive streaming events
        console.log('🔗 Emitting chat:join for session:', data.sessionId)
        socketService.emit('chat:join', data.sessionId)
      } else {
        console.log('🔄 Already joined session:', data.sessionId)
      }
    })

    socketService.on('chat:message:start', (data: MessageStartData) => {
      console.log('🚀 DEBUG: message:start received for messageId:', data.messageId, 'sessionId:', data.sessionId)

      // Get current state to determine agent type
      const currentState = store.getState()
      const hrActiveSessionId = currentState.hrChat.activeSessionId
      const opsActiveSessionId = currentState.opsChat.activeSessionId

      console.log('🔍 DEBUG: Session comparison - received:', data.sessionId, 'hrActive:', hrActiveSessionId, 'match:', data.sessionId === hrActiveSessionId)

      // Handle based on agent type
      if (hrActiveSessionId && data.sessionId === hrActiveSessionId) {
        // Check if streaming message already exists in socketSlice to avoid duplicate
        const currentState = store.getState()
        const existingStreamingMessage = currentState.socket.streamingMessages[data.messageId]


        // Only create WebSocket state if not already created
        if (!existingStreamingMessage) {
          console.log('✅ DEBUG: Creating streaming message for HR agent, messageId:', data.messageId)
          dispatch(messageStreamStarted(data))
        } else {
          console.log('⚠️ DEBUG: Streaming message already exists, messageId:', data.messageId)
        }
        
        dispatch(updateStreamingMessageId({
          sessionId: data.sessionId,
          newMessageId: data.messageId
        }))
      } else if (opsActiveSessionId && data.sessionId === opsActiveSessionId) {
        // Check if message already exists (created immediately when sent)
        const currentState = store.getState()
        const existingMessage = currentState.opsChat.sessions[opsActiveSessionId]?.messages?.find(
          msg => msg.id === data.messageId
        )
        
        // Check if streaming message already exists in socketSlice to avoid duplicate
        const existingStreamingMessage = currentState.socket.streamingMessages[data.messageId]
        
        // Only create WebSocket state if not already created
        if (!existingStreamingMessage) {
          dispatch(messageStreamStarted(data))
        }
        
        // Only create message if it doesn't exist (avoid duplicate)
        if (!existingMessage) {
          dispatch(startStreamingMessage({
            sessionId: data.sessionId,
            messageId: data.messageId
          }))
        }
      } else {
        console.warn('⚠️ Received message:start but no matching active session found', {
          messageId: data.messageId,
          sessionId: data.sessionId,
          hrActiveSessionId,
          opsActiveSessionId
        })
      }
    })

    socketService.on('chat:message:chunk', (data: MessageChunkData) => {
      
      // Get current state to determine agent type
      const currentState = store.getState()
      const hrActiveSessionId = currentState.hrChat.activeSessionId
      const opsActiveSessionId = currentState.opsChat.activeSessionId
      
      
      // Handle chunk based on agent type
      if (hrActiveSessionId && data.sessionId === hrActiveSessionId) {
        // HR Agent - use existing HR chunk handling
        dispatch(messageChunkReceived(data))
      } else if (opsActiveSessionId && (opsActiveSessionId in currentState.opsChat.sessions || opsActiveSessionId.startsWith('temp-session-'))) {
        // Ops Agent - use new Ops chunk handling
        dispatch(addStreamingChunk({
          sessionId: opsActiveSessionId,
          messageId: data.messageId,
          chunk: data.chunk
        }))
      } else {
        // Fallback to general chunk handling
        console.log('📝 Using fallback messageChunkReceived for:', data.messageId)
        dispatch(messageChunkReceived(data))
      }
    })

    socketService.on('chat:message:citation', (data: CitationData) => {
      dispatch(messageCitationReceived(data))
    })

    socketService.on('chat:message:chart', (data: any) => {
      
      // Get current state to determine which agent session is active
      const currentState = store.getState()
      const opsActiveSessionId = currentState.opsChat.activeSessionId
      
      // Only handle chart events for ops sessions
      if (opsActiveSessionId && (opsActiveSessionId in currentState.opsChat.sessions || opsActiveSessionId.startsWith('temp-session-'))) {
        console.log('📈 Adding chart to Ops message:', data.messageId)
        
        // Import the action dynamically to avoid circular dependencies
        import('@/store/slices/opsChatSlice').then(({ addChartToMessage }) => {
          dispatch(addChartToMessage({
            sessionId: opsActiveSessionId,
            messageId: data.messageId,
            chart: data.chart
          }))
        })
      }
    })

    socketService.on('chat:message:hierarchical_table', (data: any) => {
      
      // Get current state to determine which agent session is active
      const currentState = store.getState()
      const opsActiveSessionId = currentState.opsChat.activeSessionId
      
      // Only handle hierarchical table events for ops sessions
      if (opsActiveSessionId && (opsActiveSessionId in currentState.opsChat.sessions || opsActiveSessionId.startsWith('temp-session-'))) {
        console.log('📊 Adding hierarchical table to Ops message:', data.messageId)
        
        // Import the action dynamically to avoid circular dependencies
        import('@/store/slices/opsChatSlice').then(({ addHierarchicalTableToMessage }) => {
          dispatch(addHierarchicalTableToMessage({
            sessionId: opsActiveSessionId,
            messageId: data.messageId,
            table: data.table
          }))
        })
      }
    })

    socketService.on('chat:status', (data: StatusData) => {
      console.log('📊 Received status:', data.status)
      dispatch(messageStatusUpdated(data))
    })

    socketService.on('chat:message:complete', (data: MessageCompleteData) => {
      console.log('📋 Citations received:', data.citations?.length || 0)
      console.log('📋 MessageSources received:', data.messageSources?.length || 0)
      
      dispatch(messageStreamCompleted(data))
      
      // Get current active session from Redux state and determine agent type
      const currentState = store.getState()
      const hrActiveSessionId = currentState.hrChat.activeSessionId
      const opsActiveSessionId = currentState.opsChat.activeSessionId
      
      // Check if this is an HR chat session
      if (hrActiveSessionId && hrActiveSessionId in currentState.hrChat.sessions) {
        dispatch(updateMessage({
          sessionId: hrActiveSessionId,
          messageId: data.messageId,
          message: {
            content: data.fullContent,
            isStreaming: false,
            // Include both sources formats for compatibility
            sources: data.messageSources || [],
            messageSources: data.messageSources || [],
            // Also store the streaming citations for display
            streamingCitations: data.citations || []
          }
        }))
      }
      // Check if this is an Ops chat session  
      else if (opsActiveSessionId && (opsActiveSessionId in currentState.opsChat.sessions || opsActiveSessionId.startsWith('temp-session-'))) {
        console.log('📊 Updating Ops chat message:', data.messageId)
        dispatch(updateStreamingMessage({
          sessionId: opsActiveSessionId,
          messageId: data.messageId,
          content: data.fullContent,
          isComplete: true,
          metadata: {
            powerbi_queries: data.citations || []
          }
        }))
      }
      else {
        console.warn('⚠️ Received message:complete but no matching active session found', {
          messageId: data.messageId,
          hrActiveSessionId,
          opsActiveSessionId
        })
      }
    })

    socketService.on('chat:message:end', (data: { messageId: string; timestamp: Date }) => {
      
      // Get current state to determine agent type
      const currentState = store.getState()
      const hrActiveSessionId = currentState.hrChat.activeSessionId
      const opsActiveSessionId = currentState.opsChat.activeSessionId
      
      console.log('🏁 Session detection:', {
        hrActiveSessionId,
        opsActiveSessionId,
        hrSessionsKeys: Object.keys(currentState.hrChat.sessions),
        opsSessionsKeys: Object.keys(currentState.opsChat.sessions)
      })
      
      // Handle completion based on agent type
      if (hrActiveSessionId && hrActiveSessionId in currentState.hrChat.sessions) {
        console.log('🏁 Using HR completion handling for message:', data.messageId)
        // HR Agent - use existing HR completion handling
        dispatch(messageStreamCompleted({ 
          messageId: data.messageId, 
          fullContent: '', 
          citations: [], 
          timestamp: data.timestamp 
        }))
      } else if (opsActiveSessionId && (opsActiveSessionId in currentState.opsChat.sessions || opsActiveSessionId.startsWith('temp-session-'))) {
        console.log('🟢 STREAMING END EVENT: Completing streaming for message:', data.messageId)
        console.log('🟢 This should clear BOTH header and message indicators')
        console.log('🟢 WebSocket streaming messages before completion:', Object.keys(currentState.socket.streamingMessages).length)
        console.log('🟢 Active streams before completion:', currentState.socket.activeStreams)
        
        // Ops Agent - clear WebSocket streaming state first
        const streamingMessage = currentState.socket.streamingMessages[data.messageId]
        console.log('🟢 Found streaming message:', !!streamingMessage, streamingMessage?.isStreaming)
        
        dispatch(messageStreamCompleted({ 
          messageId: data.messageId, 
          fullContent: streamingMessage?.content || '', 
          citations: streamingMessage?.citations || [], 
          timestamp: data.timestamp 
        }))
        console.log('🟢 COMPLETION: Dispatched messageStreamCompleted for WebSocket state (should clear header indicator)')
        
        dispatch(opsStreamingCompleted({ 
          messageId: data.messageId
        }))
        console.log('🟢 COMPLETION: Dispatched opsStreamingCompleted for Redux state (should clear message indicator)')
      } else {
        // Fallback to HR handling for unknown cases
        dispatch(messageStreamCompleted({ 
          messageId: data.messageId, 
          fullContent: '', 
          citations: [], 
          timestamp: data.timestamp 
        }))
      }
    })

    socketService.on('chat:message:error', (data: MessageErrorData) => {
      dispatch(messageStreamError(data))
      
      // Also dispatch to the appropriate chat slice to update message state
      const currentState = store.getState()
      const hrActiveSessionId = currentState.hrChat.activeSessionId
      const opsActiveSessionId = currentState.opsChat.activeSessionId
      
      if (opsActiveSessionId && data.messageId) {
        dispatch(streamingError({
          messageId: data.messageId,
          error: data.error
        }))
      }
    })

    socketService.on('chat:typing', (data) => {
      if (data.isTyping) {
        dispatch(userTypingStarted({
          sessionId: sessionIdRef.current || '',
          userId: data.userId
        }))
      } else {
        dispatch(userTypingStopped({
          sessionId: sessionIdRef.current || '',
          userId: data.userId
        }))
      }
    })

    socketService.on('chat:session-updated', (data: { sessionId: string; title: string; updatedAt: string }) => {
      console.log('📝 Received session:updated for sessionId:', data.sessionId)
      dispatch(updateSessionTitle({
        sessionId: data.sessionId,
        title: data.title,
        updatedAt: data.updatedAt
      }))
    })

    socketService.on('error', (data) => {
      console.error('Socket error:', data.message)
      dispatch(connectionError({ error: data.message }))
    })

    // Cleanup old typing indicators periodically
    const typingCleanupInterval = setInterval(() => {
      dispatch(clearOldTypingIndicators())
    }, 30000) // Every 30 seconds

    return () => {
      // Cleanup all event listeners
      cleanupFunctions.current.forEach(cleanup => cleanup())
      cleanupFunctions.current = []

      // Remove socket event listeners
      socketService.off('chat:session-created')
      socketService.off('chat:joined')
      socketService.off('chat:message:start')
      socketService.off('chat:message:chunk')
      socketService.off('chat:message:citation')
      socketService.off('chat:message:chart')
      socketService.off('chat:message:hierarchical_table')
      socketService.off('chat:status')
      socketService.off('chat:message:complete')
      socketService.off('chat:message:end')
      socketService.off('chat:message:error')
      socketService.off('chat:session-updated')
      socketService.off('chat:typing')
      socketService.off('error')

      clearInterval(typingCleanupInterval)
      
      if (typingTimeout.current) {
        clearTimeout(typingTimeout.current)
      }
    }
  }, [dispatch])

  // Auto-connect if enabled and token is available
  useEffect(() => {
    if (autoConnect && token && !connection.connected && !connection.connecting) {
      connect()
    }
  }, [autoConnect, token]) // Remove connect from dependencies

  // Auto-join session if specified and connected (but not temp sessions)
  useEffect(() => {
    if (sessionId && 
        !sessionId.startsWith('temp-session-') && 
        connection.connected) {
      joinSession(sessionId)
    }
  }, [sessionId]) // Remove joinSession from dependencies

  // Cleanup on unmount (but not temp sessions)
  useEffect(() => {
    return () => {
      if (sessionId && 
          !sessionId.startsWith('temp-session-') && 
          connection.connected) {
        leaveSession(sessionId)
      }
    }
  }, [sessionId, connection.connected]) // Remove leaveSession from dependencies

  return {
    connect,
    disconnect,
    joinSession,
    leaveSession,
    sendMessage,
    sendTyping,
    cancelMessage,
    isConnected
  }
}

// Convenience hook for connection status
export const useWebSocketConnection = () => {
  const connection = useAppSelector(state => state.socket.connection)
  
  return {
    ...connection,
    isOnline: connection.connected,
    isOffline: !connection.connected && !connection.connecting,
    isReconnecting: connection.reconnecting,
    hasError: !!connection.error
  }
}

// Convenience hook for streaming messages in a session
export const useStreamingMessages = (sessionId: string) => {
  const allStreamingMessages = useAppSelector(state => state.socket.streamingMessages)
  const typing = useAppSelector(state => state.socket.typing[sessionId] || {})
  
  const streamingMessages = useMemo(() => {
    return Object.values(allStreamingMessages).filter(msg => msg.sessionId === sessionId)
  }, [allStreamingMessages, sessionId])
  
  const hasActiveStreams = useMemo(() => {
    const activeCount = streamingMessages.filter(msg => msg.isStreaming).length
    const hasActive = activeCount > 0
    return hasActive
  }, [streamingMessages, sessionId])
  
  const typingUsers = useMemo(() => {
    return Object.keys(typing).filter(userId => typing[userId].isTyping)
  }, [typing])
  
  const getStreamingMessage = useCallback((messageId: string) => {
    return streamingMessages.find(msg => msg.id === messageId)
  }, [streamingMessages])
  
  return {
    streamingMessages,
    hasActiveStreams,
    typingUsers,
    getStreamingMessage
  }
}

export default useWebSocket